import {
  AggregatedStoppage,
  DurationInterval,
  IssueGroupingKey,
  IssueSortingKey,
  TimeStep,
  useAggregatedStoppages,
  mapStoppageCodes
} from '@domain/stoppages'
import {useQueryParamValue} from '@hconnect/common/hooks'
import {StoppageKind, StoppageType} from '@hconnect/common/types'
import {DateRange} from '@hconnect/uikit'
import {orderBy} from 'lodash'
import {useCallback, useState} from 'react'

import {DowntimeAnalyticsEventName, TOP_ISSUES_GROUP_BY, TOP_ISSUES_SORT_BY} from '../consts'

import {useTrackDowntimeDashboardAnalytics} from './useTrackDowntimeDashboardAnalytics'

type GroupingAnalyticsEventName =
  | DowntimeAnalyticsEventName.UserSet1LevelGrouping
  | DowntimeAnalyticsEventName.UserSet2LevelGrouping

type SortingAnalyticsEventName =
  | DowntimeAnalyticsEventName.UserSet1LevelSorting
  | DowntimeAnalyticsEventName.UserSet2LevelSorting
  | DowntimeAnalyticsEventName.UserSet3LevelSorting

const buildAggregatedStoppagesPayload = ({
  timeRange,
  topIssuesGroupingKey,
  selectedStoppedMainEquipments,
  selectedMainEquipments,
  selectedEquipmentNumbers,
  selectedStoppages,
  selectedStoppageCodes,
  stoppageType,
  durationInterval,
  timeStep
}: Pick<
  SortedStoppagesProps,
  | 'timeRange'
  | 'selectedStoppedMainEquipments'
  | 'selectedMainEquipments'
  | 'selectedEquipmentNumbers'
  | 'selectedStoppages'
  | 'selectedStoppageCodes'
  | 'stoppageType'
  | 'durationInterval'
  | 'timeStep'
> & {topIssuesGroupingKey: IssueGroupingKey}) => ({
  from: timeRange.from,
  to: timeRange.to,
  groupBy: topIssuesGroupingKey,
  ...(selectedStoppedMainEquipments && {
    stoppedDepartmentUpmIds: selectedStoppedMainEquipments
  }),
  ...(selectedMainEquipments && {mainEquipmentNumbers: selectedMainEquipments}),
  ...(selectedEquipmentNumbers && {equipmentNumbers: selectedEquipmentNumbers}),
  ...(selectedStoppages && {kinds: selectedStoppages}),
  ...(selectedStoppageCodes && {stoppageCodes: mapStoppageCodes(selectedStoppageCodes)}),
  ...(stoppageType && {stoppageType}),
  ...(durationInterval && {durationInterval}),
  ...(topIssuesGroupingKey === IssueGroupingKey.TIME_INTERVAL && {timeStep})
})

type SortedStoppagesProps = {
  plantId: string
  groupBy?: IssueGroupingKey
  groupingEventName?: GroupingAnalyticsEventName
  sortingEventName?: SortingAnalyticsEventName
  timeRange: DateRange<Date>
  selectedStoppedMainEquipments?: string[]
  selectedMainEquipments?: string[]
  selectedStoppages?: StoppageKind[]
  selectedStoppageCodes?: string[]
  selectedEquipmentNumbers?: string[]
  timeStep?: TimeStep
  stoppageType?: StoppageType[]
  durationInterval?: DurationInterval
}

export const useSortedStoppages = ({
  plantId,
  groupBy,
  groupingEventName,
  sortingEventName,
  timeRange,
  selectedStoppedMainEquipments,
  selectedMainEquipments,
  selectedStoppages,
  selectedStoppageCodes,
  selectedEquipmentNumbers,
  timeStep,
  stoppageType,
  durationInterval
}: SortedStoppagesProps) => {
  const trackAnalyticsEvent = useTrackDowntimeDashboardAnalytics(plantId)

  const [groupingKey, setGroupingKey] = useQueryParamValue<IssueGroupingKey>(TOP_ISSUES_GROUP_BY, {
    keepInLocalStorage: true
  })
  const [topIssuesGroupingKey, setTopIssuesGroupingKey] = useState(
    groupBy || groupingKey || IssueGroupingKey.STOPPED_MAIN_EQUIPMENT
  )
  const [sortingKey, setSortingKey] = useQueryParamValue<IssueSortingKey>(TOP_ISSUES_SORT_BY, {
    keepInLocalStorage: true,
    enabled: topIssuesGroupingKey !== IssueGroupingKey.TIME_INTERVAL
  })
  const [topIssuesSortingKey, setTopIssuesSortingKey] = useState(
    sortingKey || IssueSortingKey.DURATION
  )

  const aggregatedStoppagesQuery = useAggregatedStoppages(
    plantId,
    buildAggregatedStoppagesPayload({
      timeRange,
      topIssuesGroupingKey,
      selectedStoppedMainEquipments,
      selectedMainEquipments,
      selectedEquipmentNumbers,
      selectedStoppages,
      selectedStoppageCodes,
      stoppageType,
      durationInterval,
      timeStep
    })
  )

  let entries: AggregatedStoppage[] = []
  if (aggregatedStoppagesQuery.data) {
    if (topIssuesGroupingKey === IssueGroupingKey.TIME_INTERVAL) {
      entries = orderBy(aggregatedStoppagesQuery.data, 'timeStepStart', 'desc')
    } else {
      if (topIssuesSortingKey === IssueSortingKey.DURATION) {
        entries = orderBy(aggregatedStoppagesQuery.data, 'hoursStopped', 'desc')
      }
      if (topIssuesSortingKey === IssueSortingKey.FREQUENCY) {
        entries = orderBy(aggregatedStoppagesQuery.data, 'stoppagesCount', 'desc')
      }
    }
  }

  const handleSetTopIssuesGroupingKey = useCallback(
    (groupBy: IssueGroupingKey) => {
      if (groupingEventName) {
        trackAnalyticsEvent(groupingEventName, {group: groupBy})
      }
      setGroupingKey(groupBy)
      setTopIssuesGroupingKey(groupBy)
    },
    [groupingEventName, trackAnalyticsEvent, setGroupingKey]
  )

  const handleSetTopIssuesSortingKey = useCallback(
    (sortBy: IssueSortingKey) => {
      if (sortingEventName) {
        trackAnalyticsEvent(sortingEventName, {sorting: sortBy})
      }
      setSortingKey(sortBy)
      setTopIssuesSortingKey(sortBy)
    },
    [sortingEventName, trackAnalyticsEvent, setSortingKey]
  )

  return {
    topIssuesSortingKey,
    setTopIssuesSortingKey: handleSetTopIssuesSortingKey,
    topIssuesGroupingKey,
    setTopIssuesGroupingKey: handleSetTopIssuesGroupingKey,
    sortedStoppages: entries,
    aggregatedStoppagesQuery
  }
}
